/**
 * REUSABLE HOOK - useFilters
 * Custom hook for filtering and searching data
 * Can be used in any React project with filterable data
 */

import { useState, useMemo } from 'react';

/**
 * useFilters - Filtering hook
 * @param {Array} data - Data to filter
 * @param {Object} initialFilters - Initial filter values
 * @returns {Object} Filter state and methods
 */
export const useFilters = (data = [], initialFilters = {}) => {
  const [filters, setFilters] = useState(initialFilters);
  const [searchTerm, setSearchTerm] = useState('');

  // Update a single filter
  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Update multiple filters
  const updateFilters = (newFilters) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
  };

  // Reset all filters
  const resetFilters = () => {
    setFilters(initialFilters);
    setSearchTerm('');
  };

  // Reset a single filter
  const resetFilter = (key) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
  };

  // Filter data based on filters and search
  const filteredData = useMemo(() => {
    let result = [...data];

    // Apply filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '' || value === 'all') {
        return; // Skip empty filters
      }

      result = result.filter((item) => {
        // Handle nested keys (e.g., 'user.name')
        const itemValue = key.includes('.')
          ? key.split('.').reduce((obj, k) => obj?.[k], item)
          : item[key];

        // Handle different value types
        if (Array.isArray(value)) {
          return value.includes(itemValue);
        }

        if (typeof value === 'boolean') {
          return itemValue === value;
        }

        if (typeof value === 'object' && value.min !== undefined && value.max !== undefined) {
          // Range filter
          return itemValue >= value.min && itemValue <= value.max;
        }

        // String comparison (case insensitive)
        return String(itemValue).toLowerCase() === String(value).toLowerCase();
      });
    });

    // Apply search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter((item) => {
        // Search in all string fields
        return Object.values(item).some((value) => {
          if (typeof value === 'string') {
            return value.toLowerCase().includes(search);
          }
          if (typeof value === 'number') {
            return String(value).includes(search);
          }
          return false;
        });
      });
    }

    return result;
  }, [data, filters, searchTerm]);

  // Get active filter count
  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter(
      (value) => value !== null && value !== undefined && value !== '' && value !== 'all'
    ).length + (searchTerm ? 1 : 0);
  }, [filters, searchTerm]);

  // Check if any filter is active
  const hasActiveFilters = activeFilterCount > 0;

  return {
    // Data
    filteredData,
    originalData: data,

    // Filters
    filters,
    searchTerm,
    activeFilterCount,
    hasActiveFilters,

    // Methods
    updateFilter,
    updateFilters,
    resetFilters,
    resetFilter,
    setSearchTerm,
  };
};

export default useFilters;
